import styled, { useTheme } from "styled-components";
import SearchGlass from "../assets/SearchGlass.svg";
import { TOOL_BAR_HEIGHT } from "../contexts/Constants";
import { useGlobalContext } from "../contexts/Constants";
import { ViewGridButton, ViewListButton } from "../assets/Buttons";
import SortBy from "./SortBy";
import { useEffect, useMemo, useState } from "react";
import { StaffFilter } from "./StaffFilter";

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

const Container = styled.div`
  padding: 0 2rem;
  height: ${TOOL_BAR_HEIGHT};
  border: solid #e0e0e0 0.03125rem;
  border-style: solid none;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 767px) {
    padding: 0 0.75rem;
    gap: 0.5rem;
  }
`;

const SearchBox = styled.div`
  height: 3rem;
  flex: 1;
  min-width: 120px;
  max-width: 450px;
  position: relative;

  @media (max-width: 900px) {
    display: none;
  }
`;

const SearchBoxGlass = styled.img`
  position: absolute;
  margin: 0.9375rem 1rem;
  padding: 0.0625rem;
  pointer-events: none;
`;

const SearchBoxInput = styled.input`
  height: 3rem;
  width: 100%;
  padding: 0.75rem 3rem;
  border: solid ${({ theme }) => theme.secondaryGrey} 0.0625rem;
  border-radius: 1.5rem;
  box-sizing: border-box;
`;

const ViewText = styled.span<{ $active?: boolean }>`
  color: ${({ $active, theme }) =>
    $active ? theme.white : theme.tertiaryGrey};
  margin-left: 0.5rem;
  @media (max-width: 1200px) {
    display: none;
  }
`;

const ViewToggle = styled.div`
  height: 3rem;
  width: 13.5625rem;
  border: solid ${({ theme }) => theme.secondaryGrey} 0.0625rem;
  background-color: ${({ theme }) => theme.white};
  border-radius: 3.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: auto;
    min-width: 7rem;
  }
`;

const ToolLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  overflow: visible;
  height: 3rem;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const ToolRight = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  flex: 0 0 auto;
  height: 3rem;

  @media (max-width: 900px) {
    gap: 0.75rem;
  }

  @media (max-width: 767px) {
    gap: 0.5rem;
  }
`;

function Toolbar() {
  const {
    gridView,
    setGridView,
    searchQuery,
    setSearchQuery,
    isMobile,
    selectedStaffFilter,
    setSelectedStaffFilter,
    linkedUsers,
    userData,
  } = useGlobalContext();

  const { width } = useWindowSize();
  const isWideScreen = width > 900;

  const staffOptions = useMemo(() => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEEAD",
      "#D4A5A5",
      "#9B59B6",
      "#3498DB",
    ];

    const allStaff = [
      { name: userData.name, color: colors[0] },
      ...linkedUsers.map((user, index) => ({
        name: user.name,
        color: colors[(index + 1) % colors.length],
      })),
    ];

    return allStaff;
  }, [linkedUsers, userData]);

  const theme = useTheme();

  return (
    <Container>
      <ToolLeft>
        {isMobile && <SortBy />}
        <SearchBox>
          <SearchBoxGlass src={SearchGlass} alt="Search Glass Icon" />
          <SearchBoxInput
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBox>
        {!isWideScreen && (
          <StaffFilter
            options={staffOptions}
            value={selectedStaffFilter}
            onChange={setSelectedStaffFilter}
            compact
          />
        )}
      </ToolLeft>
      <ToolRight>
        {isWideScreen && (
          <StaffFilter
            options={staffOptions}
            value={selectedStaffFilter}
            onChange={setSelectedStaffFilter}
          />
        )}
        {!isMobile && <SortBy />}
        <ViewToggle>
          <ViewListButton active={!gridView} onClick={() => setGridView(false)}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.8333 7.16667V2.16667H2.16667V7.16667H13.8333ZM13.8333 8.83333H2.16667V13.8333H13.8333V8.83333ZM1.33333 0.5H14.6667C15.1269 0.5 15.5 0.8731 15.5 1.33333V14.6667C15.5 15.1269 15.1269 15.5 14.6667 15.5H1.33333C0.8731 15.5 0.5 15.1269 0.5 14.6667V1.33333C0.5 0.8731 0.8731 0.5 1.33333 0.5Z"
                fill={!gridView ? theme.white : theme.tertiaryGrey}
              />
            </svg>
            <ViewText $active={!gridView}>List</ViewText>
          </ViewListButton>
          <ViewGridButton active={gridView} onClick={() => setGridView(true)}>
            <svg
              width="18"
              height="16"
              viewBox="0 0 18 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.33317 4.66667H15.6665V2.16667H2.33317V4.66667ZM10.6665 13.8333V6.33333H7.33317V13.8333H10.6665ZM12.3332 13.8333H15.6665V6.33333H12.3332V13.8333ZM5.6665 13.8333V6.33333H2.33317V13.8333H5.6665ZM1.49984 0.5H16.4998C16.9601 0.5 17.3332 0.8731 17.3332 1.33333V14.6667C17.3332 15.1269 16.9601 15.5 16.4998 15.5H1.49984C1.0396 15.5 0.66667 15.1269 0.66667 14.6667V1.33333C0.66667 0.8731 1.0396 0.5 1.49984 0.5Z"
                fill={gridView ? theme.white : theme.tertiaryGrey}
              />
            </svg>
            <ViewText $active={gridView}>Grid</ViewText>
          </ViewGridButton>
        </ViewToggle>
      </ToolRight>
    </Container>
  );
}

export default Toolbar;
